import { useRef, useState } from 'react'
import { Icon, useClickOutside } from '@carrent/ui'
import { SCROLLABLE, SETTINGS_SCHEMA, SIZE_LABELS, WIDGET_COMPONENTS } from './registry'

/**
 * Рамка гаджета: шапка, меню управления и все состояния содержимого.
 *
 * Перетаскивать можно только за шапку. Если сделать draggable всё тело,
 * внутри перестанет работать скролл списка и выделение текста — браузер
 * будет считать это началом переноса.
 */

function WidgetSkeleton() {
  return (
    <div className="widget__skeleton" aria-hidden="true">
      <span />
      <span />
      <span />
    </div>
  )
}

function WidgetMenu({
  widget,
  spec,
  canEdit,
  busy,
  onResize,
  onToggle,
  onMove,
  onSettings,
  onRemove,
  position,
  count,
}) {
  const [open, setOpen] = useState(false)
  const [confirming, setConfirming] = useState(false)
  const ref = useRef(null)
  useClickOutside(ref, () => {
    setOpen(false)
    setConfirming(false)
  }, open)

  if (!canEdit) return null

  const sizes = spec?.sizes || []
  const hasSettings = Boolean(SETTINGS_SCHEMA[widget.type])

  return (
    <div className="widget__menu" ref={ref}>
      <button
        type="button"
        className="widget__menu-btn"
        aria-label={`Настройки гаджета «${spec?.title || widget.type}»`}
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        <Icon name="more" size={18} />
      </button>

      {open && (
        <div className="widget__menu-panel" role="menu">
          {sizes.length > 1 && (
            <div className="widget__menu-section">
              <span className="widget__menu-title">Размер</span>
              <div className="seg">
                {sizes.map((s) => (
                  <button
                    key={s}
                    type="button"
                    className={`seg__btn ${widget.size === s ? 'is-active' : ''}`}
                    aria-pressed={widget.size === s}
                    onClick={() => onResize(s)}
                  >
                    {SIZE_LABELS[s]}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="widget__menu-section">
            <span className="widget__menu-title">Порядок</span>
            <div className="widget__menu-row">
              <button
                type="button"
                className="btn btn--ghost btn--sm"
                disabled={position === 0}
                aria-label="Переместить выше"
                onClick={() => onMove(-1)}
              >
                <Icon name="chevronLeft" size={16} /> Выше
              </button>
              <button
                type="button"
                className="btn btn--ghost btn--sm"
                disabled={position === count - 1}
                aria-label="Переместить ниже"
                onClick={() => onMove(1)}
              >
                Ниже <Icon name="chevronRight" size={16} />
              </button>
            </div>
          </div>

          {hasSettings && (
            <button
              type="button"
              className="widget__menu-item"
              role="menuitem"
              disabled={busy}
              onClick={() => {
                setOpen(false)
                onSettings()
              }}
            >
              <Icon name="filter" size={16} /> Настроить
            </button>
          )}

          <button
            type="button"
            className="widget__menu-item"
            role="menuitem"
            onClick={() => {
              setOpen(false)
              onToggle()
            }}
          >
            <Icon name="close" size={16} /> Отключить
          </button>

          {/* Удаление стирает настройки гаджета безвозвратно, поэтому
              спрашиваем. Отключение рядом в этом же меню и обратимо —
              шаг подтверждения заодно подсказывает, что нужно чаще. */}
          {confirming ? (
            <div className="widget__menu-confirm">
              <span className="widget__menu-confirm-text">
                Удалить вместе с настройками?
              </span>
              <div className="widget__menu-row">
                <button
                  type="button"
                  className="btn btn--danger btn--sm"
                  onClick={() => {
                    setOpen(false)
                    setConfirming(false)
                    onRemove()
                  }}
                >
                  Удалить
                </button>
                <button
                  type="button"
                  className="btn btn--ghost btn--sm"
                  onClick={() => setConfirming(false)}
                >
                  Отмена
                </button>
              </div>
            </div>
          ) : (
            <button
              type="button"
              className="widget__menu-item widget__menu-item--danger"
              role="menuitem"
              onClick={() => setConfirming(true)}
            >
              Удалить с панели
            </button>
          )}
        </div>
      )}
    </div>
  )
}

/**
 * Подпись периода в шапке — одна на все гаджеты.
 *
 * Раньше каждый писал период по-своему внутри тела («за 30 дн.»,
 * «period_days»), и на соседних карточках один и тот же срез выглядел
 * по-разному. Настройка живёт в раскладке, поэтому и читается оттуда.
 */
const PERIOD_LABELS = { 7: '7 дней', 30: '30 дней', 90: '90 дней' }

function widgetScope(widget) {
  const parts = []
  const period = widget.settings?.period
  if (period && PERIOD_LABELS[period]) parts.push(PERIOD_LABELS[period])

  const days = widget.settings?.days
  if (days) parts.push(`${days} дн. вперёд`)

  return parts.join(' · ')
}

export function WidgetShell({
  widget,
  spec,
  result,
  loading,
  updatedAt,
  stale,
  canEdit,
  position,
  count,
  onResize,
  onToggle,
  onMove,
  onSettings,
  onRemove,
  onRetry,
  dragHandlers,
  isDragging,
}) {
  const Component = WIDGET_COMPONENTS[widget.type]
  const title = spec?.title || widget.type
  const scope = widgetScope(widget)

  // «Данные устарели» — состояние гаджета, а не только панели: человек
  // смотрит в конкретную цифру и должен видеть рядом, свежая ли она.
  const freshness = stale
    ? 'данные устарели'
    : updatedAt
      ? updatedAt.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })
      : null

  const body = () => {
    if (!Component) {
      return <p className="widget-empty">Гаджет больше не поддерживается</p>
    }
    if (result?.status === 'disabled_by_admin') {
      return (
        <p className="widget-empty">
          Гаджет отключён администратором. Настройки сохранены — когда его
          включат обратно, всё вернётся как было.
        </p>
      )
    }
    if (result?.status === 'error' || result?.status === 'unknown_type') {
      return (
        <div className="widget-error">
          <p>Данные не загрузились</p>
          <button type="button" className="btn btn--ghost btn--sm" onClick={onRetry}>
            Повторить
          </button>
        </div>
      )
    }
    if (!result && loading) return <WidgetSkeleton />
    if (!result) return <WidgetSkeleton />
    return <Component data={result.data} settings={widget.settings} />
  }

  return (
    <article
      className={`widget widget--${widget.size} ${isDragging ? 'is-dragging' : ''}`}
      aria-label={title}
    >
      <header
        className="widget__head"
        draggable={canEdit}
        tabIndex={canEdit ? 0 : undefined}
        aria-label={
          canEdit
            ? `${title}. Перетащите за эту область или переставьте сочетанием Ctrl со стрелками`
            : undefined
        }
        {...(canEdit ? dragHandlers : {})}
      >
        {canEdit && (
          <span className="widget__grip" aria-hidden="true">
            <Icon name="more" size={14} />
          </span>
        )}
        <span className="widget__heading">
          <h3 className="widget__title">{title}</h3>
          {scope ? <span className="widget__scope">{scope}</span> : null}
        </span>
        {freshness ? (
          <span className={`widget__freshness ${stale ? 'is-stale' : ''}`}>
            {freshness}
          </span>
        ) : null}
        <WidgetMenu
          widget={widget}
          spec={spec}
          canEdit={canEdit}
          busy={loading && !result}
          position={position}
          count={count}
          onResize={onResize}
          onToggle={onToggle}
          onMove={onMove}
          onSettings={onSettings}
          onRemove={onRemove}
        />
      </header>

      <div className={`widget__body ${SCROLLABLE.has(widget.type) ? 'widget__body--scroll' : ''}`}>
        {body()}
      </div>
    </article>
  )
}
